// IconButton — 1:1 port of legacy `src/ui/Primitives.jsx:37–52`.
// aria-haspopup / aria-expanded / aria-controls / onKeyDown) and a
// forwarded ref so it can serve as a <Menu> trigger element directly.
import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from 'react';
import { Icon, type IconName } from './Icon.js';

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label' | 'type'
> {
  icon: IconName;
  label: string;
  size?: number;
  kind?: 'quiet';
  active?: boolean;
  style?: CSSProperties;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = 28, kind = 'quiet', active, style, onMouseEnter, onMouseLeave, ...rest },
  ref,
) {
  const bg = active ? 'var(--accent-1-subtle)' : 'transparent';
  const col = active ? 'var(--accent-1-press)' : kind === 'quiet' ? 'var(--fg-2)' : 'var(--fg-1)';
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      {...rest}
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--r-1)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: col,
        background: bg,
        transition: 'background var(--dur-1) var(--ease-out), color var(--dur-1) var(--ease-out)',
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'var(--bg-2)';
        e.currentTarget.style.color = 'var(--fg-1)';
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = bg;
        e.currentTarget.style.color = col;
        onMouseLeave?.(e);
      }}
    >
      <Icon name={icon} size={Math.min(18, size - 10)} />
    </button>
  );
});
